import { useEffect, useId, useRef, type ReactNode } from 'react';

/**
 * A modal, on the platform's own `<dialog>`.
 *
 * `showModal()` gives focus containment, Escape to close, the background made
 * inert, and top-layer stacking — four things a hand-rolled modal gets subtly
 * wrong, usually in the direction of trapping a keyboard user inside a page they
 * cannot leave. `onClose` fires for Escape as well as for the buttons, so
 * dismissing is one path rather than two.
 */

export interface DialogProps {
  title: string;
  description?: string | undefined;
  onClose: () => void;
  children: ReactNode;
  /**
   * `panel` (P11.7): a sheet from the right edge, for a row's detail and the
   * forms that act on it — its body scrolls, its actions stay in `footer`, and a
   * close button sits in the header. `dialog` is for confirmations only.
   */
  variant?: 'dialog' | 'panel';
  /** A panel's actions, kept in view however long its body is. */
  footer?: ReactNode;
  /** A small line above a panel's title: what kind of thing it is. */
  eyebrow?: string | undefined;
}

export function Dialog({
  title,
  description,
  onClose,
  children,
  variant = 'dialog',
  footer,
  eyebrow,
}: DialogProps): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);
  /**
   * True while this component is tearing itself down.
   *
   * `HTMLDialogElement.close()` fires a `close` event, and this component calls it
   * on unmount — so without this flag its own cleanup reports a cancellation.
   * Under React's StrictMode, which mounts every effect twice in development, that
   * meant the dialog opened and closed itself in the same frame and the action
   * behind it was abandoned before anyone saw it. Only running the real app showed
   * this: the tests render without StrictMode.
   */
  const unmounting = useRef(false);
  const titleId = useId();
  const descriptionId = `${titleId}-description`;

  useEffect(() => {
    const element = ref.current;
    if (element === null) return;
    unmounting.current = false;
    // Guarded because jsdom does not implement `showModal`; `test/setup.ts` shims
    // enough of it for the tests, and a browser has the real thing.
    if (typeof element.showModal === 'function' && !element.open) element.showModal();
    return () => {
      unmounting.current = true;
      if (element.open) element.close();
    };
  }, []);

  /** Escape, a `close()` from elsewhere — but never this component's own teardown. */
  const reportClose = (): void => {
    if (!unmounting.current) onClose();
  };

  /**
   * The `close` event is queued, not fired inside `close()`: under StrictMode the
   * teardown's close arrives after the second mount has opened the dialog again
   * and cleared `unmounting`. A dialog that is open has not closed, so that late
   * event is ignored — found running the real app in Chrome (P11.1), where every
   * dialog shut itself the moment it opened in development.
   */
  const closed = (): void => {
    if (ref.current?.open !== true) reportClose();
  };

  if (variant === 'panel') {
    return (
      <dialog
        ref={ref}
        className="dialog side-panel"
        aria-labelledby={titleId}
        aria-describedby={description === undefined ? undefined : descriptionId}
        onCancel={(event) => {
          event.preventDefault();
          reportClose();
        }}
        onClose={closed}
      >
        <header className="side-panel-header">
          <div>
            {eyebrow !== undefined && <p className="eyebrow">{eyebrow}</p>}
            <h2 id={titleId}>{title}</h2>
            {description !== undefined && <p id={descriptionId}>{description}</p>}
          </div>
          <button type="button" className="icon-button" aria-label="Close" onClick={reportClose}>
            ×
          </button>
        </header>
        <div className="side-panel-body">{children}</div>
        {footer !== undefined && <footer className="side-panel-footer">{footer}</footer>}
      </dialog>
    );
  }

  return (
    <dialog
      ref={ref}
      className="dialog"
      aria-labelledby={titleId}
      aria-describedby={description === undefined ? undefined : descriptionId}
      // Escape closes a native dialog by itself; this is how the component hears.
      onCancel={(event) => {
        event.preventDefault();
        reportClose();
      }}
      onClose={closed}
    >
      <div className="dialog-body">
        <div>
          <h2 id={titleId}>{title}</h2>
          {description !== undefined && <p id={descriptionId}>{description}</p>}
        </div>
        {children}
      </div>
    </dialog>
  );
}
